/**
 * "Send us your photos" — no uploader here on purpose. The website editor
 * already accepts images and is the real destination; this panel's job is to
 * tell the client *which* photos are worth sending, then get out of the way.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Panel, PanelBody, PanelHeader } from '../../ui/kit';
import { TaskDone, TaskShell } from '../TaskShell';
import type { SetupStatus } from '../../../lib/setupFlow';
import type { TaskRecord } from '../../../lib/setupTasks';

const SUPPORT_EMAIL = 'hello@foundlio.com';

const WANTED = [
  'Two or three finished jobs — the kind you would show a neighbour.',
  'Your truck or van, with the livery visible.',
  'You and the crew. Faces beat stock photos for trust.',
  'Your logo, biggest version you have.',
];

export default function PhotosTask({
  status,
  record,
  editorHref,
  onBack,
  onComplete,
}: {
  status: SetupStatus;
  record?: TaskRecord;
  editorHref: string;
  onBack: () => void;
  onComplete: (data: Record<string, string>) => void;
}) {
  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    `Photos ${status.orderRef} — ${status.businessName}`
  )}&body=${encodeURIComponent('Photos attached.\n\n')}`;

  if (record?.completedAt) {
    return (
      <TaskShell eyebrow="Step · photos" title="Photos are with us." onBack={onBack}>
        <TaskDone
          title="Got them"
          detail="We'll place them across the site and swap out any stock we used as a placeholder."
          onBack={onBack}
        />
      </TaskShell>
    );
  }

  return (
    <TaskShell
      eyebrow="Step · photos"
      title="Send us your photos."
      description="This is the single biggest difference between a site that looks like yours and one that looks like everyone's. Five good photos is plenty."
      onBack={onBack}
    >
      <Panel>
        <PanelHeader title="What works best" />
        <PanelBody className="py-2">
          <ul>
            {WANTED.map((item) => (
              <li key={item} className="flex items-start gap-3 border-b border-zinc-200 py-3 last:border-b-0">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                <span className="text-sm leading-snug text-primary">{item}</span>
              </li>
            ))}
          </ul>
        </PanelBody>
      </Panel>

      <Panel>
        <PanelBody className="space-y-3">
          <p className="text-sm leading-relaxed text-muted">
            Upload them straight into your site in the editor, or just email them — whichever is
            less friction on your phone.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to={editorHref}
              className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
            >
              Open the editor
            </Link>
            <a
              href={mailto}
              className="rounded-full border border-zinc-300 px-5 py-2.5 text-sm font-semibold text-primary transition-colors hover:border-primary"
            >
              Email the photos
            </a>
          </div>
        </PanelBody>
      </Panel>

      <button
        type="button"
        onClick={() => onComplete({ summary: 'Photos sent' })}
        className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500 transition-colors hover:text-primary"
      >
        I&apos;ve sent them — mark this done →
      </button>
    </TaskShell>
  );
}
